import { Injectable } from '@angular/core';
import { RequestsProvider } from '../requests/requests';
import { StorageProvider } from '../storage/storage';
import { NetworkProvider } from '../network/network';
import { LoggerWrapper } from '../logger/logger-wrapper.service';
import { LoggerFactory } from '../logger/logger-factory.service';
import { environment } from '../../../../environments/environment';
import dayjs, { Dayjs } from 'dayjs';
import { UserService } from '../auth/user.service';
import { OtResponse } from '../../../api/models/ot-response';

@Injectable({ providedIn: 'root' })
export class TourneeProvider {
  private logger: LoggerWrapper;

  constructor(
    private userService: UserService,
    private requestsProvider: RequestsProvider,
    private storageProvider: StorageProvider,
    private networkProvider: NetworkProvider,
    private loggerFactory: LoggerFactory
  ) {
    this.logger = this.loggerFactory.getInstance(this);
  }

  async storeOtsInCache(ots: OtResponse[]) {
    for (let i in ots) {
      const ot = ots[i];
      await this.storageProvider.set(ot.id_ot, ot);
    }
  }

  /**
   * Récupération de la tournéé
   *
   * @returns Promise qui renvoie un array d'objets activité
   */
  async getTournee(
    idUser: string,
    team: boolean,
    date_plan_debut: string = null,
    date_plan_fin: string = null
  ): Promise<OtResponse[]> {
    try {
      let urlToCall = this.buildGetTourneeUrl(idUser, team, date_plan_debut, date_plan_fin);

      let ots: OtResponse[];

      if (await this.networkProvider.isReallyConnected()) {
        // Passage de get à getOffline => Modification faite pour mettre à jour la tournée quand on revient sur la page (cas d'un nouvel ot créé)
        // Mais pas sûr que ce soit la solution adéquate
        ots = await this.requestsProvider.get(urlToCall);
      } else {
        if (team) return [];
        urlToCall =
          environment.apis.API_DACTECH_BACK_ROOT +
          '/v2/ots?idIntervenant=' +
          idUser +
          '&statut=a_faire&weekBefore=1&weekAfter=1';
        const storageName = this.storageProvider.getStorageName(urlToCall);
        ots = await this.storageProvider.get(storageName, true);
        if (date_plan_debut && date_plan_fin) {
          ots = ots.filter(
            (ot) =>
              dayjs(ot.planification_date_debut) >= dayjs(date_plan_debut) &&
              dayjs(ot.planification_date_fin) <= dayjs(date_plan_fin)
          );
        }
      }

      await this.storeOtsInCache(ots);
      return await this.updateOtsWithDraft(ots);
    } catch (err) {
      this.logger.warn('error getTournee', err);
      return [];
    }
  }

  /**
   * On met a jour l'OT offline avec le brouillon déjà crée
   */
  async updateOtsWithDraft(ots: OtResponse[]) {
    let storedOts = await this.storageProvider.get('DraftOt');
    if (storedOts === null || ots == null || ots.length == 0) {
      return ots;
    } else {
      return ots.map((ot) => {
        let storedOt = storedOts.find((el) => ot.id_ot === el.id);

        if (storedOt !== undefined) {
          return storedOt;
        } else {
          return ot;
        }
      });
    }
  }

  buildGetTourneeUrl(idUser: string, team: boolean, date_plan_debut: string, date_plan_fin: string) {
    let urlToCall: string;
    const query_date_plan_debut = date_plan_debut ?? dayjs().startOf('week').subtract(5, 'weeks').toISOString();
    const query_date_plan_fin = date_plan_fin ?? dayjs().startOf('week').add(4, 'weeks').toISOString();
    urlToCall = environment.apis.API_DACTECH_BACK_ROOT;
    urlToCall += `/v2/ots/search?planificationApresDate=${query_date_plan_debut}`;
    urlToCall += `&planificationAvantDate=${query_date_plan_fin}`;
    urlToCall += `&idIntervenant=${idUser}`;
    urlToCall += `&team=${team}`;
    urlToCall += '&statut=a_faire';
    return urlToCall;
  }

  async getOtsP1Semaine(): Promise<OtResponse[]> {
    const idUser = this.userService.getCurrentUser()!.id;
    const startDate: Dayjs = dayjs().startOf('week').add(1, 'day');
    const endDate: Dayjs = dayjs().endOf('week').add(1, 'day');

    return (await this.getTournee(idUser, false, startDate.toISOString(), endDate.toISOString())).filter(
      (ot) => ot.hasReleveP1
    );
  }
}
